import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SnGrid from "@shared/components/Grid/SnGrid";
import { showDialog } from "@widgets/CommonDialog";
import { fnSave } from "@shared/api/restApi";
import { stripBlanksFromSaveData } from "../stripBlanks";
import type { ExcelUpload_S02_Params } from "@widgets/commonpopup/excel-upload/types";

/**
 * S02 (심플/안정)
 * - S01 validRows를 컬럼정의대로 그대로 주입
 * - effGrouping으로 묶어 전표번호 채번, 전표별 line 1..N
 * - 모든 행 state='insert' 마킹 → getChange()가 insert로 잡힘
 * - 저장: getChange() 그대로 tables.header/detail 키로 전송
 */

type S02WithModalProps = ExcelUpload_S02_Params & {
  modalRef?: React.RefObject<any>;
};

/**
 * 헤더(pur_poh)에 라인(pur_pol) 합계로 올려야 하는 금액 항목.
 * 그룹의 첫 행 값을 그대로 쓰면 라인이 2건 이상일 때 헤더 금액이 어긋난다.
 */
const HEADER_SUM_FIELDS = ["am", "am_k", "am_vat", "am_supply"];

/** 문자열/콤마 포함 값도 안전하게 숫자로 (numeric(20,4) 기준 4자리 반올림) */
const toNum = (v: any) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};
const round4 = (n: number) => Math.round(n * 10000) / 10000;

const ExcelUpload_S02_ReviewSave = forwardRef<any, S02WithModalProps>(
  ({ modalRef, ...props }, ref) => {
    const {
      validRows,
      headerColumns,
      detailColumns,
      grouping,
      controller,
      numbering,
      tables,
      fieldMap, // { headerNoField, detailNoField, lineNoField } (필수)
    } = props;

    // === 필수 필드명 ===
    const headerNoField = fieldMap?.headerNoField!;
    const detailNoField = fieldMap?.detailNoField!;
    const lineNoField = fieldMap?.lineNoField!;
    const [popupParent, setPopupParent] = useState<any>(null);

    // === 컬럼 필드 목록 (useMemo로 고정) ===
    const headerFields = useMemo(
      () =>
        (headerColumns ?? [])
          .map((c: any) => String(c?.field ?? ""))
          .filter((f) => f.length > 0),
      [headerColumns]
    );

    const detailFields = useMemo(
      () =>
        (detailColumns ?? [])
          .map((c: any) => String(c?.field ?? ""))
          .filter((f) => f.length > 0),
      [detailColumns]
    );

    // === 유효 그룹핑: 시스템 키 제외 (useMemo 고정) ===
    const effGrouping = useMemo(
      () =>
        (grouping ?? []).filter(
          (f) =>
            !!f &&
            f !== headerNoField &&
            f !== detailNoField &&
            f !== lineNoField
        ),
      [grouping, headerNoField, detailNoField, lineNoField]
    );

    const makeKey = (row: any) =>
      effGrouping.map((f) => row?.[f] ?? "").join("||");

    // numbering 안정화 (의존성 최소화)
    const numberingFn = numbering.fn;
    const { cd_module, no_line, cd_prefix, cd_menu, dateField } =
      numbering.options;

    // === 그리드 refs / state ===
    const gridApiRefH = useRef<any>(null);
    const gridApiRefL = useRef<any>(null);

    // === 그룹핑 (useMemo) ===
    const groups = useMemo(() => {
      const map = new Map<string, any[]>();
      for (const r of validRows) {
        const k = makeKey(r);
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(r);
      }
      return map;
    }, [validRows, effGrouping]);
    useEffect(() => {
      if (modalRef?.current) {
        setPopupParent(modalRef.current.getPopupParent());
      }
    }, [modalRef?.current]);
    // === 데이터 구성: 헤더/디테일 ===
    useEffect(() => {
      let cancelled = false;

      (async () => {
        // 필수 필드 누락 가드
        if (!headerNoField || !detailNoField || !lineNoField) {
          await showDialog(
            "alert",
            "fieldMap(headerNoField/detailNoField/lineNoField)이 누락되었습니다."
          );
          return;
        }

        if (!dateField) {
          await showDialog("alert", "전표채번용 날짜항목이 누락되었습니다.");
          return;
        }

        const H: any[] = [];
        const L: any[] = [];

        for (const [, rows] of Array.from(groups.entries())) {
          const sample = rows[0] ?? {};

          // 전표 채번 (dateField의 "값"을 넣어서 호출)
          const slipNo = await numberingFn(
            cd_module,
            no_line,
            cd_prefix,
            sample?.[dateField],
            cd_menu
          );
          if (cancelled) return;

          // 헤더 1행: headerColumns.field 기준으로 "있는 그대로" 채우고 slipNo 주입
          // 단, 금액 항목은 그룹에 속한 라인들의 합계로 덮어쓴다.
          const sums: any = {};
          HEADER_SUM_FIELDS.forEach((f) => {
            sums[f] = round4(
              rows.reduce((acc: number, r: any) => acc + toNum(r?.[f]), 0)
            );
          });

          const hdr = { ...sample, ...sums, [headerNoField]: slipNo };
          H.push(hdr);

          // 디테일: rows 각각을 detailColumns.field 기준으로 채우고 slipNo/line 부여
          let seq = 0;
          for (const r of rows) {
            seq += 1;
            const d: any = {
              ...r,
              [detailNoField]: slipNo,
              [lineNoField]: seq,
            };

            L.push(d);
          }
        }

        if (cancelled) return;

        // 그리드 비우고
        await gridApiRefH.current?.deleteAllRows?.();
        await gridApiRefL.current?.deleteAllRows?.();

        // 헤더 추가 (=> modifiedRows.added에 자동 기록)
        for (const hdr of H) {
          gridApiRefH.current?.addRow(hdr);
        }

        // 디테일 추가 (=> modifiedRows.added에 자동 기록)
        for (const row of L) {
          gridApiRefL.current?.addRow(row);
        }

        // 첫 전표 기준으로 디테일 필터/포커스
        if (H.length > 0) {
          const slip = H[0][headerNoField];
          gridApiRefH.current?.selectAndFocusRow(0);
          gridApiRefL.current?.applyFilters({ [detailNoField]: slip });
          gridApiRefL.current?.selectAndFocusRow(0);
        }

        // 렌더 후 포커스/필터 + 모든 행 state='insert' 마킹
        setTimeout(() => {
          if (H.length > 0) {
            gridApiRefH.current?.selectAndFocusRow(0);
            const slip = H[0]?.[headerNoField];
            if (slip) {
              gridApiRefL.current?.applyFilters({ [detailNoField]: slip });
              gridApiRefL.current?.selectAndFocusRow(0);
            }
          }
        }, 0);
      })();

      return () => {
        cancelled = true;
      };
    }, [
      groups, // validRows/effGrouping 바뀔 때만 변경
      headerFields, // useMemo로 고정
      detailFields, // useMemo로 고정
      headerNoField,
      detailNoField,
      lineNoField,
      numberingFn, // 함수 레퍼런스
      cd_module,
      no_line,
      cd_prefix,
      cd_menu,
      dateField, // 원시값
    ]);

    // === 상단 포커스 → 하단 필터 ===
    const onFocusedRowChanged = (row: any) => {
      if (!row) return;
      const slip = row?.[headerNoField];
      gridApiRefL.current?.applyFilters({ [detailNoField]: slip });
      gridApiRefL.current?.selectAndFocusRow(0);
    };

    // === 저장 ===
    const onSave = async (): Promise<boolean> => {
      if (!gridApiRefH.current || !gridApiRefL.current) return false;
      const soh = gridApiRefH.current.getChange();
      const sol = gridApiRefL.current.getChange();

      if (
        (soh?.modifiedRowcount ?? 0) === 0 &&
        (sol?.modifiedRowcount ?? 0) === 0
      ) {
        await showDialog("alert", "변경된 내용이 없습니다.");
        return false;
      }

      const payload: Record<string, any> = {
        [String(tables.header)]: soh,
        [String(tables.detail)]: sol,
      };

      // 저장 전 공백 제거 (코드/번호 계열은 안쪽 공백까지, 그 외는 앞뒤만)
      const rtn: boolean = await fnSave(
        controller,
        stripBlanksFromSaveData(payload)
      );

      if (rtn) {
        gridApiRefH.current?.resetUpdate();
        gridApiRefL.current?.resetUpdate();

        return true;
      }

      return false;
    };

    useImperativeHandle(
      ref,
      () => ({
        getSelectedValue: async () => {
          return await onSave();
        },
      }),
      [onSave]
    );

    return (
      <div className="h-full flex flex-col">
        {/* 헤더 */}
        <div className="flex-1 flex flex-col gap-2 min-h-00">
          <div className="flex-[2] min-h-0">
            <SnGrid
              ref={gridApiRefH}
              columnConfig={headerColumns}
              popupParent={popupParent}
              rowData={[]}
              rowSelection="single"
              isEditable={true}
              enableAddRow={false}
              onFocusedRowChanged={onFocusedRowChanged}
            />
          </div>

          {/* 디테일 */}
          <div className="flex-[3] min-h-100">
            <SnGrid
              ref={gridApiRefL}
              columnConfig={detailColumns}
              rowData={[]}
              rowSelection="single"
              isEditable={true}
              enableAddRow={false}
              ischeckboxSelection={false}
            />
          </div>
        </div>
      </div>
    );
  }
);

export default ExcelUpload_S02_ReviewSave;
